import { createContext, useCallback, useEffect, useState } from "react";
import { errorToast } from "../utils/toastUtils";
import { ERRORS } from "../constants/errors";
import { useNavigate } from "react-router-dom";
import { JWT, Route, UserContextType } from "../constants/types";
import { getSessionStorage } from "../utils/storageUtils";
import {
  AUTH_HEADER,
  SESSION_STORAGE_AUTH_FLAG,
  SESSION_STORAGE_TOKEN,
} from "../../keys";
import { decodeJwt } from "../utils/tokenUtils";
import { decrypt } from "../utils/cryptoUtils";
import ROUTE_CONSTANTS from "../constants/routeConstants";
import DashboardIcon from "@mui/icons-material/Dashboard";
import { setGlobalHeader } from "../utils/apiUtils";

const defaultUserContext: UserContextType = {
  isUserAuthenticated: false,
  userData: {
    role: "",
    id: "",
  },
  setDataOnLogin: () => {},
  setDataOnLogout: () => {},
  userRoutes: [
    {
      url: "/dashboard",
      name: "Dashboard",
      sequenceNumber: 0,
      icon: <DashboardIcon />,
    },
  ],
  setUserRoutes: () => {},
};

export const UserContext = createContext(defaultUserContext);

const UserContextProvider = ({ children }: { children: any }) => {
  const navigate = useNavigate();
  const [isUserAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [userRoutes, setUserRoutes] = useState<Route[]>(
    defaultUserContext.userRoutes
  );
  const [userData, setUserData] = useState<{
    role?: string;
    id?: string;
  }>({
    role: "",
    id: "",
  });
  const setDataOnLogin = useCallback(
    (userDetails: { role?: string; id?: string }) => {
      try {
        setUserData(userDetails);
      } catch (error: any) {
        errorToast(error?.message ?? ERRORS.BAD_REQUEST);
        navigate("/");
      }
    },
    [navigate]
  );

  const setDataOnLogout = useCallback(() => {
    setUserRoutes([]);
    setUserData(defaultUserContext.userData);
    navigate(ROUTE_CONSTANTS.HOME);
  }, [navigate]);

  const checkLogin = useCallback(async (): Promise<boolean> => {
    try {
      const isLoggedIn = getSessionStorage(SESSION_STORAGE_AUTH_FLAG);
      const sessionStorageToken = getSessionStorage(SESSION_STORAGE_TOKEN);
      const decryptedToken: string = decrypt(sessionStorageToken);
      if (isLoggedIn === "true" && decryptedToken) {
        const jwtData: JWT = decodeJwt(decryptedToken) ?? {
          aud: "",
          exp: "",
          iat: "",
          roleConst: "",
          roleId: "",
          userId: "",
        };
        if (
          userData.role !== jwtData.roleConst ||
          userData.id !== jwtData.userId
        ) {
          setDataOnLogin({
            role: jwtData.roleConst,
            id: jwtData.userId,
          });
          setGlobalHeader(AUTH_HEADER, decryptedToken);
        }
      }
      return isLoggedIn === "true";
    } catch (error: any) {
      errorToast(error?.message ?? ERRORS.BAD_REQUEST);
      setDataOnLogout();
      return false;
    }
  }, [setDataOnLogin, setDataOnLogout, userData.id, userData.role]);

  useEffect(() => {
    (async () => setIsAuthenticated(await checkLogin()))();
  }, [checkLogin, isUserAuthenticated, userData]);

  const userContextValue: UserContextType = {
    isUserAuthenticated: isUserAuthenticated,
    userData,
    setDataOnLogin,
    setDataOnLogout,
    userRoutes,
    setUserRoutes,
  };

  return (
    <UserContext.Provider value={userContextValue}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContextProvider;
